import React, { useEffect, useState } from 'react';
import { useToastStore, type ToastItem } from '../stores/toastStore';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

const ICONS: Record<string, string> = {
  message: '💬', like: '❤️', comment: '💭', wall: '📝', system: '🔔',
};

const ToastCard: React.FC<{ toast: ToastItem; onClose: (id: string) => void }> = ({ toast, onClose }) => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onClose(toast.id), 300);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleClick = () => {
    if (toast.fromUserId) {
      if (toast.type === 'message') navigate('/messages');
      else navigate(`/profile/${toast.fromUserId}`);
    }
    setVisible(false);
    setTimeout(() => onClose(toast.id), 300);
  };

  const formatTime = (dateString: string) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'только что';
    if (m < 60) return `${m} мин`;
    const h = Math.floor(diff / 3600000);
    return h < 24 ? `${h} ч` : `${Math.floor(diff / 86400000)} дн`;
  };

  return (
    <div
      onClick={handleClick}
      className={`flex items-start gap-3 bg-[#1a1d24] border border-gray-700 rounded-2xl p-4 shadow-2xl cursor-pointer transition-all duration-300 hover:border-gray-500 ${
        visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <span className="text-2xl">{ICONS[toast.type] || '🔔'}</span>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-bold">{toast.title}</p>
        {toast.content && <p className="text-gray-400 text-xs truncate">{toast.content}</p>}
        <p className="text-gray-600 text-[10px] mt-1">{formatTime(toast.createdAt)}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onClose(toast.id); }}
        className="text-gray-600 hover:text-white transition shrink-0"
      >✕</button>
    </div>
  );
};

const ToastNotification: React.FC = () => {
  const { toasts, enabled, removeToast } = useToastStore();
  if (!enabled || toasts.length === 0) return null;

  return createPortal(
    <div className="fixed top-4 right-4 z-[10000] flex flex-col gap-2 w-80">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onClose={removeToast} />
      ))}
    </div>,
    document.body
  );
};

export default ToastNotification;
